// Helpers to navigate the auto-generated folder tree (content-tags).
//
// The CMS auto-generates a "Clientes" container folder whose direct children are
// one folder per client (each carries `clientId`); inside each client folder the
// brand folders live (each carries `brandId`). Users can nest further subfolders
// manually below a brand. See tags_with_autogenerates_clients.json for a sample
// and docs/contents-flow.md for the model.

// Depth-first search for the first node matching a predicate.
const findNode = (nodes, predicate) => {
  for (const node of nodes || []) {
    if (predicate(node)) return node
    const found = findNode(node.children, predicate)
    if (found) return found
  }
  return null
}

// Path (root → target) of folder nodes to the tag id within the folder tree.
// Returns [] when not found (e.g. at root, no folder selected).
export const findFolderPath = (nodes, targetId) => {
  for (const node of nodes || []) {
    if (String(node.id) === String(targetId)) return [node]
    if (node.children && node.children.length) {
      const sub = findFolderPath(node.children, targetId)
      if (sub.length) return [node, ...sub]
    }
  }
  return []
}

// Collapse a folder path for breadcrumbs: when it has more than `max` folders
// (default 3), keep the first and last and replace the middle with an ellipsis
// marker. Returns a list of items, each either { folder } or
// { ellipsis: true, hidden: [...folders] } (hidden = the folders the ellipsis
// stands in for, handy for a tooltip).
export const collapsePath = (path = [], max = 3) => {
  if (path.length <= max) return path.map((folder) => ({ folder }))
  return [
    { folder: path[0] },
    { ellipsis: true, hidden: path.slice(1, -1) },
    { folder: path[path.length - 1] },
  ]
}

// Items shown on a folder card: its direct ads (`itemsCount`, from the API's
// contents_count) plus its direct subfolders. Direct level only — it does not
// recurse into the subfolders' own contents.
export const countFolderItems = (folder) =>
  (folder.itemsCount || 0) + (folder.children ? folder.children.length : 0)

// The client folder anywhere in the tree (folders are mapped with `clientId`).
export const findFolderByClientId = (folders, clientId) =>
  clientId == null ? null : findNode(folders, (n) => String(n.clientId) === String(clientId))

// The brand folder within a given subtree (brand folders carry `brandId`).
export const findFolderByBrandId = (nodes, brandId) =>
  brandId == null ? null : findNode(nodes, (n) => String(n.brandId) === String(brandId))

// The auto-generated "Clientes" container: the folder that groups the per-client
// folders (i.e. its direct children carry a `clientId`). Identified structurally
// rather than by name so it survives localization/renames.
export const findClientsContainer = (folders) =>
  findNode(folders, (n) => (n.children || []).some((c) => c.clientId != null))

// Resolve the folder that should be auto-selected for a client + brand pick:
//   1. the brand folder inside the client folder, when the brand is associated;
//   2. otherwise the client folder (brand not associated under this client);
//   3. otherwise the "Clientes" container (client has no folder of its own).
// Returns the folder id (the `tag`) or null when none of the above exist.
export const resolveDefaultTag = (folders, clientId, brandId) => {
  const clientFolder = findFolderByClientId(folders, clientId)
  if (clientFolder) {
    const brandFolder = findFolderByBrandId(clientFolder.children, brandId)
    if (brandFolder) return brandFolder.id
    return clientFolder.id
  }
  const container = findClientsContainer(folders)
  return container ? container.id : null
}
